import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useWidgetDispatch, useWidgetSelector } from "../../../store/store";
import { NumericFormat } from "react-number-format";
import { usePlausible } from "next-plausible";
import { PortableText } from "@portabletext/react";
import AnimateHeight from "react-animate-height";
import { NextButton } from "../../shared/Buttons/NavigationButtons";
import { ToolTip } from "../../shared/ToolTip/ToolTip";
import { Spinner } from "../../../../Spinner/Spinner";
import { RadioButtonGroup } from "../../../../RadioButton/RadioButtonGroup";
import { EffektButton, EffektButtonVariant } from "../../../../EffektButton/EffektButton";
import { State } from "../../../store/state";
import { RecurringDonation, ShareType } from "../../../types/Enums";
import { setRecurring, setCauseAreaAmount, setOrgAmount, setCauseAreaDistributionType, setShowAllOrganizations, setHasManuallyEditedPrefilledOrgAmount, nextPane } from "../../../store/actions";
import { thousandize } from "../../../../../../../util/formatting";
import { useAmountCalculation } from "../AmountPane/useAmountCalculation";
import { AmountContext, SmartDistributionContext } from "../../../types/WidgetProps";
import { useIsMobile } from "../../../../../../../hooks/useIsMobile";
import { splitAmountByShares, standardShareByOrgId } from "../../../utils/donationCalculations";
import panes from "../Panes.module.scss";
import styles from "./SingleCauseAreaPane.module.scss";
interface SingleCauseAreaPaneProps {
  nextButtonText: string;
  enableRecurring: boolean;
  enableSingle: boolean;
  singleDonationText: string;
  monthlyDonationText: string;
  amountContext: AmountContext;
  smartDistributionContext: SmartDistributionContext;
}

/**
 * Amount pane used when a platform has a single (active) cause area, e.g. Norway.
 *
 * It reproduces the pre-rewrite ("main") widget experience — recurring toggle,
 * suggested sums, a smart/custom distribution selector with an explanatory
 * description — using main's markup and styling, but is driven by the rewritten
 * donation state. The one thing carried over from the new widget is that custom
 * organization distribution is entered as direct kroner amounts rather than
 * percentages. Operations/tip is intentionally omitted to match the old widget.
 */
export const SingleCauseAreaPane: React.FC<SingleCauseAreaPaneProps> = t0 => {
  const $ = _c(37);
  const {
    nextButtonText,
    enableRecurring,
    enableSingle,
    singleDonationText,
    monthlyDonationText,
    amountContext,
    smartDistributionContext
  } = t0;
  const dispatch = useWidgetDispatch();
  const plausible = usePlausible();
  const isMobile = useIsMobile();
  const causeAreas = useWidgetSelector(_temp);
  const {
    recurring,
    causeAreaAmounts: t1,
    orgAmounts: t2,
    causeAreaDistributionType: t3,
    prefilledShares: t4,
    showAllOrganizations: t5,
    hasManuallyEditedPrefilledOrgAmount: t6
  } = useWidgetSelector(_temp2);
  const causeAreaAmounts = t1 === undefined ? {} : t1;
  const orgAmounts = t2 === undefined ? {} : t2;
  let t7;
  if ($[0] !== t3) {
    t7 = t3 === undefined ? {} : t3;
    $[0] = t3;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const causeAreaDistributionType = t7;
  const prefilledShares = t4 === undefined ? null : t4;
  const showAllOrganizations = t5 === undefined ? false : t5;
  const hasManuallyEditedPrefilledOrgAmount = t6 === undefined ? false : t6;
  const activeCauseAreas = causeAreas?.filter(_temp3) ?? [];
  const causeArea = activeCauseAreas[0];
  let t8;
  if ($[2] !== causeAreas) {
    t8 = causeAreas ?? [];
    $[2] = causeAreas;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const {
    totalAmount
  } = useAmountCalculation("single", causeArea?.id ?? null, t8);
  const currentAmount = causeArea ? causeAreaAmounts[causeArea.id] || 0 : 0;
  const inputValue = currentAmount;
  const [showErrors, setShowErrors] = React.useState(false);
  let t9;
  if ($[4] !== prefilledShares) {
    t9 = !!prefilledShares && Object.keys(prefilledShares).length > 0;
    $[4] = prefilledShares;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const hasPrefilledOrgs = t9;
  const isTrackingPrefilledShares = hasPrefilledOrgs && !hasManuallyEditedPrefilledOrgAmount;
  const trackedOrgAmounts = isTrackingPrefilledShares && prefilledShares ? splitAmountByShares(currentAmount, prefilledShares) : {};
  const displayOrgAmount = orgId => {
    if (isTrackingPrefilledShares && prefilledShares?.[orgId] !== undefined) {
      return trackedOrgAmounts[orgId] ?? 0;
    }
    return orgAmounts[orgId] || 0;
  };
  const effectiveTotalAmount = isTrackingPrefilledShares ? currentAmount : totalAmount;
  if (!causeAreas) {
    let t10;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className={panes.pane}><div className={panes.paneContainer}><div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%"
          }}><Spinner /></div></div></div>;
      $[6] = t10;
    } else {
      t10 = $[6];
    }
    return t10;
  }
  if (!causeArea) {
    let t10;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className={panes.pane}><div className={panes.paneContainer}><div>Missing cause area</div></div></div>;
      $[7] = t10;
    } else {
      t10 = $[7];
    }
    return t10;
  }
  const activeOrganizations = causeArea.organizations.filter(_temp4).sort(_temp5);
  const hasMultipleOrgs = activeOrganizations.length > 1;
  const distributionType = causeAreaDistributionType[causeArea.id] ?? ShareType.STANDARD;
  const hideAmountInput = hasMultipleOrgs && distributionType === ShareType.CUSTOM && (!hasPrefilledOrgs || hasManuallyEditedPrefilledOrgAmount);
  const suggestedSums = recurring ? amountContext.preset_amounts_recurring : amountContext.preset_amounts_single;
  const setAmount = amount => {
    dispatch(setCauseAreaAmount(causeArea.id, amount));
    if (activeOrganizations.length <= 1 && activeOrganizations[0]) {
      dispatch(setOrgAmount(activeOrganizations[0].id, amount));
    }
  };
  const handleDistributionChange = value => {
    dispatch(setCauseAreaDistributionType(causeArea.id, value));
    if (value === ShareType.CUSTOM) {
      const hasSetOrgAmounts = activeOrganizations.some(org_0 => (orgAmounts[org_0.id] || 0) > 0);
      if (!hasSetOrgAmounts) {
        const amountByOrgId = splitAmountByShares(causeAreaAmounts[causeArea.id] || 0, standardShareByOrgId(causeArea));
        for (const org_1 of causeArea.organizations) {
          if (!org_1.standardShare) {
            continue;
          }
          dispatch(setOrgAmount(org_1.id, amountByOrgId[org_1.id] ?? 0));
        }
      }
    }
  };
  let t10;
  if ($[8] !== amountContext.custom_amount_text) {
    t10 = <label>{amountContext.custom_amount_text}</label>;
    $[8] = amountContext.custom_amount_text;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const amountInput = <><div className={styles.sumButtonsWrapper}>{suggestedSums.map(suggested => <div key={suggested.amount}><EffektButton variant={EffektButtonVariant.SECONDARY} selected={inputValue === suggested.amount} onClick={() => {
          plausible("SelectSuggestedSum", {
            props: {
              sum: suggested.amount
            }
          });
          setAmount(suggested.amount);
        }} noMinWidth={true} data-cy={`suggested-sum-${causeArea.id}-${suggested.amount}`}>{`${suggested.amount ? thousandize(suggested.amount) : "-"} kr`}</EffektButton>{suggested.subtext && <i>{suggested.subtext}</i>}</div>)}</div><div className={styles.sumWrapper}>{t10}<span><NumericFormat name={`sum-${causeArea.id}`} thousandSeparator=" " allowNegative={false} decimalScale={0} type="tel" placeholder="0" value={inputValue > 0 ? inputValue : ""} autoComplete="off" data-cy={`donation-sum-input-${causeArea.id}`} onValueChange={values => setAmount(values.floatValue ?? 0)} /></span></div></>;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h2 className={panes.paneTitle}><wbr /></h2>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const t12 = !enableSingle;
  let t13;
  if ($[11] !== singleDonationText || $[12] !== t12) {
    t13 = {
      title: singleDonationText,
      value: RecurringDonation.NON_RECURRING,
      data_cy: "radio-single",
      disabled: t12
    };
    $[11] = singleDonationText;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const t14 = !enableRecurring;
  let t15;
  if ($[14] !== monthlyDonationText || $[15] !== t14) {
    t15 = {
      title: monthlyDonationText,
      value: RecurringDonation.RECURRING,
      data_cy: "radio-recurring",
      disabled: t14
    };
    $[14] = monthlyDonationText;
    $[15] = t14;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  if ($[17] !== t13 || $[18] !== t15) {
    t16 = [t13, t15];
    $[17] = t13;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== dispatch) {
    t17 = value_0 => dispatch(setRecurring(value_0 as RecurringDonation));
    $[20] = dispatch;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== recurring || $[23] !== t16 || $[24] !== t17) {
    t18 = <RadioButtonGroup options={t16} selected={recurring} onSelect={t17} />;
    $[22] = recurring;
    $[23] = t16;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const t19 = showErrors && effectiveTotalAmount <= 0;
  let t20;
  if ($[26] !== dispatch || $[27] !== effectiveTotalAmount || $[28] !== isTrackingPrefilledShares || $[29] !== prefilledShares || $[30] !== setShowErrors || $[31] !== trackedOrgAmounts) {
    t20 = () => {
      if (effectiveTotalAmount <= 0) {
        setShowErrors(true);
        return;
      }
      if (isTrackingPrefilledShares && prefilledShares) {
        Object.keys(prefilledShares).forEach(orgId_0 => {
          dispatch(setOrgAmount(Number(orgId_0), trackedOrgAmounts[Number(orgId_0)] ?? 0));
        });
      }
      dispatch(nextPane());
    };
    $[26] = dispatch;
    $[27] = effectiveTotalAmount;
    $[28] = isTrackingPrefilledShares;
    $[29] = prefilledShares;
    $[30] = setShowErrors;
    $[31] = trackedOrgAmounts;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== nextButtonText || $[34] !== t19 || $[35] !== t20) {
    t21 = <div className={styles.actionBar}><NextButton disabled={t19} onClick={t20} data-cy="next-button">{nextButtonText}</NextButton></div>;
    $[33] = nextButtonText;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  return <div className={panes.pane}><div className={panes.paneContainer}><div>{t11}{t18}<AnimateHeight height={hideAmountInput ? 0 : "auto"} animateOpacity={true} duration={300}><div className={hasMultipleOrgs ? styles.amountInputSection : `${styles.amountInputSection} ${styles.amountInputSectionPadBottom}`}>{amountInput}</div></AnimateHeight>{hasMultipleOrgs && <div className={styles.shareSelectionSpacer}><RadioButtonGroup options={[{
            title: smartDistributionContext.smart_distribution_radiobutton_text,
            value: ShareType.STANDARD,
            data_cy: "radio-smart-share"
          }, {
            title: smartDistributionContext.custom_distribution_radiobutton_text,
            value: ShareType.CUSTOM,
            data_cy: "radio-custom-share"
          }]} selected={distributionType} onSelect={value_1 => handleDistributionChange(value_1 as ShareType)} /><AnimateHeight height={distributionType === ShareType.STANDARD && smartDistributionContext.smart_distribution_description ? "auto" : 0} animateOpacity={true} duration={300}><div className={styles.infoParagraph}>{smartDistributionContext.smart_distribution_description && <PortableText value={smartDistributionContext.smart_distribution_description} />}</div></AnimateHeight><AnimateHeight height={distributionType === ShareType.CUSTOM ? "auto" : 0} animateOpacity={true} duration={300}><div className={styles.sharesSelectorContainer}><div className={styles.shareSelectionWrapper}><div className={styles.shareContainer}>{(hasPrefilledOrgs && !showAllOrganizations ? activeOrganizations.filter(org_2 => prefilledShares?.[org_2.id] !== undefined) : activeOrganizations).map(org_3 => <div className={styles.shareInputContainer} key={org_3.id}><div><a className={styles.shareLink} href={org_3.informationUrl} target={isMobile ? "_blank" : undefined} rel={isMobile ? "noopener noreferrer" : undefined}>{org_3.widgetDisplayName || org_3.name}</a>{org_3.widgetContext && <ToolTip text={org_3.widgetContext} />}</div><NumericFormat id={`org-${org_3.id}`} aria-label={org_3.widgetDisplayName || org_3.name} type="tel" placeholder="0" value={displayOrgAmount(org_3.id) || ""} step={1} decimalScale={0} allowNegative={false} thousandSeparator=" " autoComplete="off" data-cy={`org-${org_3.id}`} onValueChange={(values_0, sourceInfo) => {
                      if (sourceInfo.source !== "event") {
                        return;
                      }
                      if (prefilledShares?.[org_3.id] !== undefined) {
                        dispatch(setHasManuallyEditedPrefilledOrgAmount(true));
                      }
                      dispatch(setOrgAmount(org_3.id, values_0.floatValue ?? 0));
                    }} /></div>)}</div>{hasPrefilledOrgs && !showAllOrganizations && <button className={styles.showAllOrganizationsLink} type="button" onClick={() => dispatch(setShowAllOrganizations(true))} data-cy="show-all-organizations-button">{smartDistributionContext.show_all_organizations_text} ↓</button>}</div></div></AnimateHeight></div>}</div>{t21}</div></div>;
};
function _temp(state) {
  return state.layout.causeAreas;
}
function _temp2(state_0) {
  return state_0.donation;
}
function _temp3(ca) {
  return ca.isActive;
}
function _temp4(org) {
  return org.isActive;
}
function _temp5(a, b) {
  return a.ordering - b.ordering;
}